import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, Check, Users, MailCheck, Mail, Star } from "lucide-react";
import { toast } from "sonner";
import { roleClass } from "../lib/roleBadge";

// Finestra di selezione destinatari suggeriti dalla pratica.
// All'avvio chiede il tipo di invio (PEC o Email); la scelta determina gli indirizzi mostrati e preselezionati.

export default function SuggeritiDestinatariDialog({ open, onClose, praticaId, mode, onConfirm }) {
  const [chosen, setChosen] = useState(null); // null => schermata di scelta; "pec" | "email"
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState({});

  // Reset della scelta a ogni apertura.
  useEffect(() => { if (open) { setChosen(null); setItems([]); setChecked({}); } }, [open]);

  useEffect(() => {
    if (!open || !praticaId || !chosen) return;
    setLoading(true); setItems([]); setChecked({});
    api.get(`/studio/pratiche/${praticaId}/destinatari-suggeriti`)
      .then(({ data }) => {
        const list = (chosen === "pec" ? data.pec : data.email) || [];
        setItems(list);
        setChecked(Object.fromEntries(list.filter((it) => it.preferito).map((it) => [it.address, true])));
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, praticaId, chosen]);

  const selCount = useMemo(() => Object.values(checked).filter(Boolean).length, [checked]);
  const toggle = (addr) => setChecked((c) => ({ ...c, [addr]: !c[addr] }));

  const confirm = () => {
    const sel = items.filter((it) => checked[it.address]).map((it) => ({ name: it.name, address: it.address }));
    if (!sel.length) { toast.error("Seleziona almeno un destinatario"); return; }
    onConfirm(sel, chosen);
    onClose();
  };

  const Icon = chosen === "pec" ? MailCheck : Users;
  const title = chosen === "pec" ? "PEC compagnie e soggetti collegati" : "Email soggetti collegati";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-popover border-border text-popover-foreground max-w-lg max-h-[80vh] flex flex-col" data-testid="suggeriti-dialog">
        {!chosen ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-2 text-foreground">
                <Mail className="h-5 w-5 text-amber-600" /> Tipo di invio
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-2.5">
              <p className="text-sm text-muted-foreground">Scegli come vuoi inviare: verranno preselezionati gli indirizzi del tipo scelto.</p>
              <button type="button" data-testid="suggeriti-choice-pec" onClick={() => setChosen("pec")}
                className={`w-full flex items-center gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors ${mode === "pec" ? "border-amber-500 bg-amber-500/10" : "border-border hover:bg-accent"}`}>
                <MailCheck className="h-5 w-5 text-amber-600 shrink-0" />
                <span><span className="block text-sm font-semibold text-foreground">Invia come PEC</span><span className="block text-[11px] text-muted-foreground">Indirizzi PEC certificati</span></span>
              </button>
              <button type="button" data-testid="suggeriti-choice-email" onClick={() => setChosen("email")}
                className={`w-full flex items-center gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors ${mode === "email" ? "border-amber-500 bg-amber-500/10" : "border-border hover:bg-accent"}`}>
                <Mail className="h-5 w-5 text-sky-500 shrink-0" />
                <span><span className="block text-sm font-semibold text-foreground">Invia come Email</span><span className="block text-[11px] text-muted-foreground">Indirizzi email ordinari</span></span>
              </button>
            </div>
            <DialogFooter className="border-t border-border pt-3">
              <Button variant="outline" onClick={onClose} data-testid="suggeriti-choice-cancel" className="ml-auto border-border text-foreground hover:bg-accent">Annulla</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-2 text-foreground">
                <Icon className="h-5 w-5 text-amber-600" /> {title}
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto -mx-1 px-1 min-h-[160px]">
              {loading ? (
                <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
              ) : items.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10" data-testid="suggeriti-empty">Nessun contatto {chosen === "pec" ? "PEC" : "email"} disponibile per questa pratica.</p>
              ) : (
                <div className="space-y-1">
                  {items.map((it) => {
                    const on = !!checked[it.address];
                    return (
                      <button key={it.address} type="button" onClick={() => toggle(it.address)} data-testid={`suggeriti-item-${it.address}`}
                        className={`w-full flex items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors ${on ? "border-amber-500 bg-amber-500/10" : "border-border hover:bg-accent"}`}>
                        <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-amber-500 border-amber-500" : "border-muted-foreground/50"}`}>
                          {on && <Check className="h-3 w-3 text-slate-900" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center gap-2">
                            <span className="text-sm text-foreground truncate">{it.name}</span>
                            {it.preferito && (
                              <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-600" title="Indirizzo preferito" data-testid={`suggeriti-pref-${it.address}`}>
                                <Star className="h-3 w-3" fill="currentColor" />Preferito
                              </span>
                            )}
                            {it.role && (
                              <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${roleClass(it.role)}`} data-testid={`suggeriti-role-${it.address}`}>
                                {it.role}
                              </span>
                            )}
                          </span>
                          <span className="block text-[11px] text-muted-foreground font-mono truncate">{it.address}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <DialogFooter className="border-t border-border pt-3">
              <div className="flex items-center gap-3 w-full">
                <span className="text-xs text-muted-foreground" data-testid="suggeriti-count">{selCount} selezionati</span>
                <Button variant="outline" onClick={() => setChosen(null)} data-testid="suggeriti-back" className="ml-auto border-border text-foreground hover:bg-accent">Indietro</Button>
                <Button onClick={confirm} data-testid="suggeriti-confirm" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">Aggiungi destinatari</Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
